import React from "react";
import { Link } from "react-router-dom";
import { trpc } from "../../lib/api/trpc";
import { usePermissions } from "../../hooks/usePermissions";
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

function StatCardSkeleton() {
  return (
    <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6 animate-pulse">
      <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
      <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accentClass,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  accentClass: string;
}) {
  return (
    <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`h-5 w-5 ${accentClass}`} />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {label}
        </span>
      </div>
      <div className={`text-3xl font-bold ${accentClass}`}>{value}</div>
    </div>
  );
}

interface AiUsageRow {
  organizationId: string;
  organizationName: string;
  day: string;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  platformCostUsd: number;
  orgKeyCostUsd: number;
  unpricedCallCount: number;
}

interface OrgAiSpendSummary {
  organizationId: string;
  organizationName: string;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  platformCostUsd: number;
  orgKeyCostUsd: number;
  unpricedCallCount: number;
}

function summarizeAiUsageByOrg(rows: AiUsageRow[]): OrgAiSpendSummary[] {
  const byOrg = new Map<string, OrgAiSpendSummary>();
  for (const row of rows) {
    const existing = byOrg.get(row.organizationId);
    if (existing) {
      existing.callCount += row.callCount;
      existing.inputTokens += row.inputTokens;
      existing.outputTokens += row.outputTokens;
      existing.cacheReadTokens += row.cacheReadTokens;
      existing.platformCostUsd += row.platformCostUsd;
      existing.orgKeyCostUsd += row.orgKeyCostUsd;
      existing.unpricedCallCount += row.unpricedCallCount;
    } else {
      byOrg.set(row.organizationId, { ...row });
    }
  }
  return [...byOrg.values()].sort(
    (a, b) => b.platformCostUsd - a.platformCostUsd,
  );
}

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

function AiSpendSection() {
  // @ts-ignore - tRPC type issues
  const { data, isLoading } = trpc.admin.getAiUsage.useQuery({ days: 30 });

  const rows: AiUsageRow[] = data?.rows ?? [];
  const orgSummaries = summarizeAiUsageByOrg(rows);
  const totalPlatformCost = orgSummaries.reduce(
    (sum, org) => sum + org.platformCostUsd,
    0,
  );

  return (
    <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6 mb-8">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          AI Spend — last 30 days (UTC)
        </h2>
        {!isLoading && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Platform total: {formatUsd(totalPlatformCost)}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="h-24 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
      ) : orgSummaries.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No AI usage recorded yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-4 font-medium">Organization</th>
                <th className="py-2 pr-4 font-medium text-right">
                  Platform spend
                </th>
                <th className="py-2 pr-4 font-medium text-right">
                  BYO-key spend
                </th>
                <th className="py-2 pr-4 font-medium text-right">Calls</th>
                <th className="py-2 pr-4 font-medium text-right">Tokens in</th>
                <th className="py-2 pr-4 font-medium text-right">Tokens out</th>
                <th className="py-2 pr-4 font-medium text-right">Cache read</th>
              </tr>
            </thead>
            <tbody>
              {orgSummaries.map((org) => (
                <tr
                  key={org.organizationId}
                  className="border-b border-gray-100 dark:border-gray-700/50 text-gray-900 dark:text-gray-100"
                >
                  <td className="py-2 pr-4">
                    {org.organizationName}
                    {org.unpricedCallCount > 0 && (
                      <span
                        className="ml-2 text-xs text-amber-600 dark:text-amber-400"
                        title="Calls whose model is missing from the pricing map — tokens recorded, cost unknown"
                      >
                        {org.unpricedCallCount} unpriced
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right font-medium">
                    {formatUsd(org.platformCostUsd)}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-500 dark:text-gray-400">
                    {formatUsd(org.orgKeyCostUsd)}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    {org.callCount.toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    {org.inputTokens.toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    {org.outputTokens.toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    {org.cacheReadTokens.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const AdminDashboardPage: React.FC = () => {
  const { hasOrgContext } = usePermissions();
  // @ts-ignore - tRPC type issues
  const { data: stats, isLoading } = trpc.admin.getGlobalStats.useQuery();

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
          Platform Administration
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          System overview across all organizations
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={BuildingOfficeIcon}
              label="Total Organizations"
              value={stats?.totalOrganizations ?? 0}
              accentClass="text-accent"
            />
            <StatCard
              icon={CheckCircleIcon}
              label="Active Organizations"
              value={stats?.activeOrganizations ?? 0}
              accentClass="text-green-600 dark:text-green-400"
            />
            <StatCard
              icon={ClockIcon}
              label="Trial Organizations"
              value={stats?.trialOrganizations ?? 0}
              accentClass="text-amber-600 dark:text-amber-400"
            />
            <StatCard
              icon={ExclamationTriangleIcon}
              label="Suspended Organizations"
              value={stats?.suspendedOrganizations ?? 0}
              accentClass="text-red-600 dark:text-red-400"
            />
            <StatCard
              icon={UserGroupIcon}
              label="Total Users"
              value={stats?.totalUsers ?? 0}
              accentClass="text-accent"
            />
          </>
        )}
      </div>

      {/* AI Spend (#144) */}
      <AiSpendSection />

      {/* Quick Actions */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/organizations"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-tint text-accent hover:text-accent-strong transition-colors"
          >
            <BuildingOfficeIcon className="w-4 h-4" />
            View Organizations
          </Link>
          <Link
            to="/admin/email-testing"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-tint text-accent hover:text-accent-strong transition-colors"
          >
            <EnvelopeIcon className="w-4 h-4" />
            Email Testing
          </Link>
          {hasOrgContext && (
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400 dark:hover:bg-gray-900/30 transition-colors"
            >
              Go to Org Dashboard
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
